import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { ESTADO_PERFIL_VEHICULO, DURACION_MEMBRESIA_PREMIUM_MESES } from '../../../constants/concesionariaConstants';
import CredencialesClienteService from '../../cliente/services/CredencialesClienteService';

/**
 * Servicio para gestionar concesionarias y sus perfiles de vehículos
 */
class ConcesionariaService {
  
  /**
   * Obtener datos de una concesionaria
   * @param {string} concesionariaId - ID de la concesionaria
   * @returns {Promise<Object>} Datos de la concesionaria
   */
  async obtenerConcesionaria(concesionariaId) {
    try {
      // Primero intentar buscar en la colección 'empresas' (donde están las concesionarias)
      const empresaRef = doc(db, 'empresas', concesionariaId);
      const empresaSnap = await getDoc(empresaRef);
      
      if (empresaSnap.exists()) {
        const empresaData = empresaSnap.data();
        // Verificar que sea una concesionaria
        const tipoEmpresa = empresaData.tipoEmpresa || empresaData.tipo_empresa || '';
        const esConcesionaria = tipoEmpresa.toLowerCase().includes('concesionari') || 
                               tipoEmpresa.toLowerCase() === 'concesionaria';
        
        if (esConcesionaria) {
          return {
            id: empresaSnap.id,
            ...empresaData
          };
        }
      }
      
      // Si no se encontró en empresas, buscar en la colección 'concesionarias' (fallback)
      const concesionariaRef = doc(db, 'concesionarias', concesionariaId);
      const concesionariaSnap = await getDoc(concesionariaRef);
      
      if (concesionariaSnap.exists()) {
        return {
          id: concesionariaSnap.id,
          ...concesionariaSnap.data()
        };
      }
      
      // Si no se encontró en ninguna colección, lanzar error
      throw new Error('Concesionaria no encontrada');
    } catch (error) {
      console.error('Error obteniendo concesionaria:', error);
      throw error;
    }
  }
  
  /**
   * Obtener concesionaria por usuario ID
   * @param {string} usuarioId - ID del usuario
   * @returns {Promise<Object|null>} Datos de la concesionaria
   */
  async obtenerConcesionariaPorUsuario(usuarioId) {
    try {
      // Buscar primero en empresas con tipoEmpresa === 'concesionaria'
      const empresasQuery = query(
        collection(db, 'empresas'),
        where('tipoEmpresa', '==', 'concesionaria'),
        where('uid', '==', usuarioId)
      );
      const empresasSnap = await getDocs(empresasQuery);
      
      if (!empresasSnap.empty) {
        return {
          id: empresasSnap.docs[0].id,
          ...empresasSnap.docs[0].data()
        };
      }
      
      // También buscar por uid_auth o uid directo
      const empresasQuery2 = query(
        collection(db, 'empresas'),
        where('tipoEmpresa', '==', 'concesionaria'),
        where('uid_auth', '==', usuarioId)
      );
      const empresasSnap2 = await getDocs(empresasQuery2);
      
      if (!empresasSnap2.empty) {
        return {
          id: empresasSnap2.docs[0].id,
          ...empresasSnap2.docs[0].data()
        };
      }
      
      // Fallback: buscar en colección concesionarias
      const q = query(
        collection(db, 'concesionarias'),
        where('usuarioId', '==', usuarioId)
      );
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        return {
          id: snapshot.docs[0].id,
          ...snapshot.docs[0].data()
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error obteniendo concesionaria por usuario:', error);
      return null;
    }
  }
  
  /**
   * Obtener estadísticas de perfiles de una concesionaria
   * @param {string} concesionariaId - ID de la concesionaria
   * @returns {Promise<Object>} Estadísticas
   */
  async obtenerEstadisticasPerfiles(concesionariaId) {
    try {
      const perfilesQuery = query(
        collection(db, 'perfiles_vehiculos_concesionaria'),
        where('concesionariaId', '==', concesionariaId)
      );
      const perfilesSnap = await getDocs(perfilesQuery);
      
      const perfiles = perfilesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      return {
        total: perfiles.length,
        preCreados: perfiles.filter(p => p.estado === ESTADO_PERFIL_VEHICULO.PRE_CREADO).length,
        asignados: perfiles.filter(p => p.estado === ESTADO_PERFIL_VEHICULO.ASIGNADO).length,
        pendientesValidacion: perfiles.filter(p => p.estado === ESTADO_PERFIL_VEHICULO.PENDIENTE_VALIDACION).length,
        activos: perfiles.filter(p => p.estado === ESTADO_PERFIL_VEHICULO.ACTIVO).length,
        expirados: perfiles.filter(p => p.estado === ESTADO_PERFIL_VEHICULO.EXPIRADO).length
      };
    } catch (error) {
      console.error('Error obteniendo estadísticas:', error);
      return {
        total: 0,
        preCreados: 0,
        asignados: 0,
        pendientesValidacion: 0,
        activos: 0,
        expirados: 0
      };
    }
  }
  
  /**
   * Obtener lotes de perfiles de una concesionaria
   * @param {string} concesionariaId - ID de la concesionaria
   * @returns {Promise<Array>} Lista de lotes
   */
  async obtenerLotes(concesionariaId) {
    try {
      const lotesQuery = query(
        collection(db, 'lotes_perfiles'),
        where('concesionariaId', '==', concesionariaId)
      );
      const lotesSnap = await getDocs(lotesQuery);
      
      return lotesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error obteniendo lotes:', error);
      return [];
    }
  }
  
  /**
   * Calcular perfiles disponibles desde lotes
   * @param {string} concesionariaId - ID de la concesionaria
   * @returns {Promise<Object>} { comprados, usados, disponibles, preCreados }
   */
  async calcularPerfilesDisponibles(concesionariaId) {
    try {
      // Obtener lotes activos
      const lotesQuery = query(
        collection(db, 'lotes_perfiles'),
        where('concesionariaId', '==', concesionariaId),
        where('activo', '==', true)
      );
      const lotesSnap = await getDocs(lotesQuery);
      
      let totalComprados = 0;
      let totalUsados = 0;
      
      lotesSnap.forEach(doc => {
        const lote = doc.data();
        totalComprados += lote.cantidad || 0;
        totalUsados += lote.cantidadUsada || 0;
      });
      
      // También verificar en los datos de la empresa/concesionaria directamente
      try {
        const concesionaria = await this.obtenerConcesionaria(concesionariaId);
        if (concesionaria.totalPerfilesComprados) {
          totalComprados = concesionaria.totalPerfilesComprados;
        }
        if (concesionaria.totalPerfilesUsados) {
          totalUsados = concesionaria.totalPerfilesUsados;
        }
      } catch (err) {
        console.log('No se pudo obtener datos de concesionaria para calcular disponibilidad:', err);
      }
      
      // Obtener perfiles pre-creados
      const perfilesPreCreadosQuery = query(
        collection(db, 'perfiles_vehiculos_concesionaria'),
        where('concesionariaId', '==', concesionariaId),
        where('estado', '==', ESTADO_PERFIL_VEHICULO.PRE_CREADO)
      );
      const perfilesPreCreadosSnap = await getDocs(perfilesPreCreadosQuery);
      const preCreados = perfilesPreCreadosSnap.size;
      
      const disponibles = Math.max(0, totalComprados - totalUsados);
      
      return {
        comprados: totalComprados,
        usados: totalUsados,
        disponibles: disponibles,
        preCreados: preCreados
      };
    } catch (error) {
      console.error('Error calculando perfiles disponibles:', error);
      return {
        comprados: 0,
        usados: 0,
        disponibles: 0,
        preCreados: 0
      };
    }
  }
  
  /**
   * Crear perfil de vehículo
   * @param {string} concesionariaId - ID de la concesionaria
   * @param {Object} datosVehiculo - Datos del vehículo
   * @param {boolean} skipVerificacionLote - Si true, no verifica disponibilidad de lotes (para uso interno)
   * @returns {Promise<string>} ID del perfil creado
   */
  async crearPerfilVehiculo(concesionariaId, datosVehiculo, skipVerificacionLote = false) {
    try {
      // Verificar que hay perfiles disponibles (excepto si se omite la verificación)
      if (!skipVerificacionLote) {
        const disponibles = await this.calcularPerfilesDisponibles(concesionariaId);
        if (disponibles.disponibles <= 0) {
          throw new Error('No hay perfiles disponibles. Debes comprar un lote primero.');
        }
      }
      
      // Obtener datos de la concesionaria
      const concesionaria = await this.obtenerConcesionaria(concesionariaId);
      
      const perfilData = {
        concesionariaId,
        concesionariaNombre: concesionaria.nombre || 'Sin nombre',
        marca: datosVehiculo.marca,
        modelo: datosVehiculo.modelo,
        año: datosVehiculo.año,
        tipoVehiculo: datosVehiculo.tipoVehiculo || 'sedan',
        vin: datosVehiculo.vin || null,
        estado: ESTADO_PERFIL_VEHICULO.PRE_CREADO,
        clienteId: null,
        clienteNombre: null,
        clienteEmail: null,
        clienteTelefono: null,
        fechaCreacion: serverTimestamp(),
        fechaAsignacion: null,
        fechaActivacion: null,
        fechaExpiracionMembresia: null,
        membresiaPremiumActiva: false,
        diasMembresiaRestantes: null,
        activo: true
      };
      
      const perfilRef = await addDoc(
        collection(db, 'perfiles_vehiculos_concesionaria'),
        perfilData
      );
      
      console.log('✅ Perfil de vehículo creado:', perfilRef.id);
      return perfilRef.id;
      
    } catch (error) {
      console.error('Error creando perfil de vehículo:', error);
      throw error;
    }
  }
  
  /**
   * Obtener perfiles de vehículos de una concesionaria
   * @param {string} concesionariaId - ID de la concesionaria
   * @param {string} estado - Estado opcional para filtrar
   * @returns {Promise<Array>} Lista de perfiles
   */
  async obtenerPerfilesVehiculos(concesionariaId, estado = null) {
    try {
      let q;
      if (estado) {
        q = query(
          collection(db, 'perfiles_vehiculos_concesionaria'),
          where('concesionariaId', '==', concesionariaId),
          where('estado', '==', estado)
        );
      } else {
        q = query(
          collection(db, 'perfiles_vehiculos_concesionaria'),
          where('concesionariaId', '==', concesionariaId)
        );
      }
      
      const perfilesSnap = await getDocs(q);
      return perfilesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error obteniendo perfiles:', error);
      return [];
    }
  }
  
  /**
   * Asignar perfil de vehículo a cliente
   * @param {string} perfilId - ID del perfil
   * @param {Object} datosCliente - Datos del cliente
   * @param {string} clienteId - ID del cliente existente (opcional)
   * @param {Object} opciones - Opciones adicionales
   * @param {string} opciones.password - Contraseña para el cliente (opcional, se genera si no se proporciona)
   * @param {boolean} opciones.asignarCredenciales - Si se deben asignar credenciales (default: true)
   * @param {Object} opciones.concesionariaNombre - Nombre de la concesionaria (requerido si asignarCredenciales es true)
   * @returns {Promise<Object>} Resultado con información de credenciales si se asignaron
   */
  async asignarVehiculoACliente(perfilId, datosCliente, clienteId = null, opciones = {}) {
    try {
      const perfilRef = doc(db, 'perfiles_vehiculos_concesionaria', perfilId);
      const perfilSnap = await getDoc(perfilRef);
      
      if (!perfilSnap.exists()) {
        throw new Error('Perfil de vehículo no encontrado');
      }
      
      const perfilData = perfilSnap.data();
      
      if (perfilData.estado !== ESTADO_PERFIL_VEHICULO.PRE_CREADO) {
        throw new Error('El perfil ya está asignado o activo');
      }
      
      // Calcular fechas de membresía premium (12 meses desde ahora)
      const fechaActivacion = new Date();
      const fechaExpiracion = new Date();
      fechaExpiracion.setMonth(fechaExpiracion.getMonth() + DURACION_MEMBRESIA_PREMIUM_MESES);
      
      // Si hay clienteId, usar datos existentes
      let clienteFinalId = clienteId;
      let clienteNombre = datosCliente.nombre;
      let clienteEmail = datosCliente.email;
      let clienteTelefono = datosCliente.telefono;
      
      if (clienteId) {
        // Obtener datos del cliente existente
        try {
          const clienteRef = doc(db, 'usuarios', clienteId);
          const clienteSnap = await getDoc(clienteRef);
          if (clienteSnap.exists()) {
            const clienteData = clienteSnap.data();
            clienteNombre = clienteData.nombre || datosCliente.nombre;
            clienteEmail = clienteData.email || datosCliente.email;
            clienteTelefono = clienteData.telefono || datosCliente.telefono;
          }
        } catch (error) {
          console.warn('No se pudo obtener datos del cliente existente:', error);
        }
      } else {
        // Si no hay clienteId, se creará uno nuevo (esto se manejará en otro lugar)
        // Por ahora solo guardamos los datos proporcionados
      }
      
      // Determinar el estado inicial: si se van a asignar credenciales, será PENDIENTE_VALIDACION
      const asignarCredenciales = opciones.asignarCredenciales !== false; // Por defecto true
      const estadoInicial = asignarCredenciales 
        ? ESTADO_PERFIL_VEHICULO.PENDIENTE_VALIDACION 
        : ESTADO_PERFIL_VEHICULO.ASIGNADO;
      
      // Actualizar perfil
      await updateDoc(perfilRef, {
        estado: estadoInicial,
        clienteId: clienteFinalId,
        clienteNombre,
        clienteEmail,
        clienteTelefono,
        fechaAsignacion: serverTimestamp(),
        fechaActivacion: asignarCredenciales ? null : Timestamp.fromDate(fechaActivacion), // Solo se activa después de validación
        fechaExpiracionMembresia: Timestamp.fromDate(fechaExpiracion),
        membresiaPremiumActiva: false, // Se activa después de validación
        diasMembresiaRestantes: DURACION_MEMBRESIA_PREMIUM_MESES * 30,
        fechaActualizacion: serverTimestamp(),
        pendienteValidacion: asignarCredenciales, // Flag para indicar que está pendiente de validación
        fechaAsignacionCredenciales: asignarCredenciales ? serverTimestamp() : null
      });
      
      // Si el cliente existe, crear/actualizar vehículo en su perfil
      if (clienteFinalId) {
        await this.crearVehiculoEnPerfilCliente(perfilData, clienteFinalId);
      }
      
      // Asignar credenciales y enviar notificación por email si está habilitado
      let resultadoCredenciales = null;
      
      if (asignarCredenciales && opciones.concesionariaNombre) {
        try {
          resultadoCredenciales = await CredencialesClienteService.asignarCredencialesYNotificar(
            {
              email: clienteEmail,
              nombre: clienteNombre,
              telefono: clienteTelefono
            },
            opciones.password || null,
            {
              perfilId: perfilId,
              marca: perfilData.marca,
              modelo: perfilData.modelo,
              año: perfilData.año,
              concesionariaNombre: opciones.concesionariaNombre,
              concesionariaId: opciones.concesionariaId || perfilData.concesionariaId || null
            }
          );
          
          // Actualizar el perfil con el UID del cliente si se crearon credenciales
          if (resultadoCredenciales && resultadoCredenciales.uid) {
            await updateDoc(perfilRef, {
              clienteId: resultadoCredenciales.uid,
              credencialesAsignadas: true,
              fechaCredencialesAsignadas: serverTimestamp()
            });
            
            // Si no había clienteId antes, ahora sí lo hay, crear vehículo en perfil
            if (!clienteFinalId) {
              await this.crearVehiculoEnPerfilCliente(perfilData, resultadoCredenciales.uid);
            }
          }
        } catch (errorCredenciales) {
          console.error('⚠️ Error asignando credenciales (continuando con asignación de vehículo):', errorCredenciales);
          // No lanzar error, solo registrar el problema
        }
      }
      
      console.log('✅ Vehículo asignado a cliente');
      
      return {
        success: true,
        clienteId: clienteFinalId || resultadoCredenciales?.uid,
        credenciales: resultadoCredenciales
      };
      
    } catch (error) {
      console.error('Error asignando vehículo a cliente:', error);
      throw error;
    }
  }
  
  /**
   * Crear vehículo en el perfil del cliente
   * @param {Object} perfilData - Datos del perfil de vehículo
   * @param {string} clienteId - ID del cliente
   * @returns {Promise<void>}
   */
  async crearVehiculoEnPerfilCliente(perfilData, clienteId) {
    try {
      const vehiculoData = {
        clienteId,
        userId: clienteId,
        marca: perfilData.marca,
        modelo: perfilData.modelo,
        año: perfilData.año,
        tipoVehiculo: perfilData.tipoVehiculo,
        vin: perfilData.vin,
        patente: null, // La concesionaria puede no tener patente aún
        creadoPorConcesionaria: true,
        concesionariaId: perfilData.concesionariaId,
        perfilConcesionariaId: perfilData.id,
        membresiaPremium: true,
        fechaActivacionPremium: new Date(),
        fechaExpiracionPremium: perfilData.fechaExpiracionMembresia?.toDate?.() || null,
        activo: true,
        fechaRegistro: new Date(),
        fechaCreacion: new Date()
      };
      
      const vehiculosRef = collection(db, 'vehiculos');
      await addDoc(vehiculosRef, vehiculoData);
      
      console.log('✅ Vehículo creado en perfil del cliente');
    } catch (error) {
      console.error('Error creando vehículo en perfil del cliente:', error);
      // No lanzar error para no bloquear el flujo principal
    }
  }
  
  /**
   * Buscar cliente por email o teléfono
   * @param {string} email - Email del cliente
   * @param {string} telefono - Teléfono del cliente
   * @returns {Promise<Object|null>} Cliente encontrado o null
   */
  async buscarCliente(email, telefono) {
    try {
      // Buscar por email
      if (email) {
        const emailQuery = query(
          collection(db, 'usuarios'),
          where('email', '==', email),
          where('rol', '==', 'cliente')
        );
        const emailSnap = await getDocs(emailQuery);
        if (!emailSnap.empty) {
          const clienteData = emailSnap.docs[0].data();
          return {
            id: emailSnap.docs[0].id,
            ...clienteData
          };
        }
      }
      
      // Buscar por teléfono
      if (telefono) {
        const telefonoQuery = query(
          collection(db, 'usuarios'),
          where('telefono', '==', telefono),
          where('rol', '==', 'cliente')
        );
        const telefonoSnap = await getDocs(telefonoQuery);
        if (!telefonoSnap.empty) {
          const clienteData = telefonoSnap.docs[0].data();
          return {
            id: telefonoSnap.docs[0].id,
            ...clienteData
          };
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error buscando cliente:', error);
      return null;
    }
  }
  
  /**
   * Eliminar perfil de vehículo (solo si está en PRE_CREADO)
   * @param {string} perfilId - ID del perfil
   * @returns {Promise<void>}
   */
  async eliminarPerfilVehiculo(perfilId) {
    try {
      const perfilRef = doc(db, 'perfiles_vehiculos_concesionaria', perfilId);
      const perfilSnap = await getDoc(perfilRef);
      
      if (!perfilSnap.exists()) {
        throw new Error('Perfil no encontrado');
      }
      
      const perfilData = perfilSnap.data();
      if (perfilData.estado !== ESTADO_PERFIL_VEHICULO.PRE_CREADO) {
        throw new Error('No se puede eliminar un perfil que ya está asignado o activo');
      }
      
      await updateDoc(perfilRef, {
        activo: false,
        fechaEliminacion: serverTimestamp()
      });
      
      console.log('✅ Perfil de vehículo eliminado');
    } catch (error) {
      console.error('Error eliminando perfil:', error);
      throw error;
    }
  }
  
  /**
   * Verificar y actualizar membresías expiradas
   * @param {string} perfilId - ID del perfil
   * @returns {Promise<boolean>} True si estaba expirado, false si no
   */
  async verificarMembresiaExpirada(perfilId) {
    try {
      const perfilRef = doc(db, 'perfiles_vehiculos_concesionaria', perfilId);
      const perfilSnap = await getDoc(perfilRef);
      
      if (!perfilSnap.exists()) {
        return false;
      }
      
      const perfilData = perfilSnap.data();
      
      if (perfilData.estado !== ESTADO_PERFIL_VEHICULO.ACTIVO) {
        return false;
      }
      
      const fechaExpiracion = perfilData.fechaExpiracionMembresia?.toDate?.() || 
                             perfilData.fechaExpiracionMembresia;
      
      if (!fechaExpiracion) {
        return false;
      }
      
      const ahora = new Date();
      if (fechaExpiracion < ahora) {
        // Membresía expirada, cambiar a estado EXPIRADO
        await updateDoc(perfilRef, {
          estado: ESTADO_PERFIL_VEHICULO.EXPIRADO,
          membresiaPremiumActiva: false,
          fechaActualizacion: serverTimestamp()
        });
        
        return true;
      }
      
      // Actualizar días restantes
      const diasRestantes = Math.ceil((fechaExpiracion - ahora) / (1000 * 60 * 60 * 24));
      await updateDoc(perfilRef, {
        diasMembresiaRestantes: diasRestantes,
        fechaActualizacion: serverTimestamp()
      });
      
      return false;
    } catch (error) {
      console.error('Error verificando membresía expirada:', error);
      return false;
    }
  }
  
  /**
   * Validar perfil de vehículo (cambiar de PENDIENTE_VALIDACION a ACTIVO)
   * @param {string} perfilId - ID del perfil a validar
   * @returns {Promise<void>}
   */
  async validarPerfilVehiculo(perfilId) {
    try {
      const perfilRef = doc(db, 'perfiles_vehiculos_concesionaria', perfilId);
      const perfilSnap = await getDoc(perfilRef);
      
      if (!perfilSnap.exists()) {
        throw new Error('Perfil de vehículo no encontrado');
      }
      
      const perfilData = perfilSnap.data();
      
      if (perfilData.estado !== ESTADO_PERFIL_VEHICULO.PENDIENTE_VALIDACION) {
        throw new Error('El perfil no está en estado pendiente de validación');
      }
      
      // Calcular fechas de membresía premium (12 meses desde ahora)
      const fechaActivacion = new Date();
      const fechaExpiracion = new Date();
      fechaExpiracion.setMonth(fechaExpiracion.getMonth() + DURACION_MEMBRESIA_PREMIUM_MESES);
      
      // Actualizar perfil a ACTIVO
      await updateDoc(perfilRef, {
        estado: ESTADO_PERFIL_VEHICULO.ACTIVO,
        membresiaPremiumActiva: true,
        fechaActivacion: Timestamp.fromDate(fechaActivacion),
        fechaExpiracionMembresia: Timestamp.fromDate(fechaExpiracion),
        diasMembresiaRestantes: DURACION_MEMBRESIA_PREMIUM_MESES * 30,
        pendienteValidacion: false,
        fechaValidacion: serverTimestamp(),
        validadoPor: perfilData.concesionariaId || null,
        fechaActualizacion: serverTimestamp()
      });
      
      console.log('✅ Perfil validado y activado:', perfilId);
      
    } catch (error) {
      console.error('Error validando perfil:', error);
      throw error;
    }
  }
}

export default new ConcesionariaService();

